import React, { useState } from "react";
import { Mail, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

// Persistent banner that prompts the user to verify their email and offers a
// resend button. Shown on the main app shell while the logged-in user has
// emailVerified === false.
const VerifyEmailBanner = () => {
  const { user, resendVerificationEmail } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [dismissed, setDismissed] = useState(false);

  if (!user) return null;
  const verified = user.emailVerified ?? user.email_verified;
  if (verified) return null;
  if (dismissed) return null;

  const handleResend = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      await resendVerificationEmail();
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Failed to send verification email");
    }
  };

  return (
    <div
      className="border-b border-gold-500/40 bg-forest-800 text-forest-100 text-sm"
      role="status"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="w-4 h-4 flex-shrink-0 text-gold-400" />
          <span className="truncate">
            {status === "sent"
              ? `Sent — check ${user.email} for the verification link.`
              : status === "error"
                ? errorMsg
                : `Please verify ${user.email} to enable cloud sync and subscriptions.`}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleResend}
            disabled={status === "sending"}
            className="px-3 py-1 rounded bg-gold-500 hover:bg-gold-400 text-forest-950 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {status === "sending"
              ? "Sending…"
              : status === "sent"
                ? "Resend again"
                : "Resend email"}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded text-forest-300 hover:text-gold-400 transition-colors"
            title="Dismiss for this session"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
